import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Star } from "lucide-react";
import { toast } from "sonner";

const ManageTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    client_name: "",
    content: "",
    rating: "5",
    event_type: "",
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const testimonialData = {
        ...formData,
        rating: parseInt(formData.rating),
      };

      await axios.post(`${API}/testimonials`, testimonialData, { headers });
      toast.success("Testimonial added successfully!");
      setIsDialogOpen(false);
      setFormData({ client_name: "", content: "", rating: "5", event_type: "" });
      fetchTestimonials();
    } catch (error) {
      console.error("Error adding testimonial:", error);
      toast.error("Failed to add testimonial");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this testimonial?")) return;

    const token = localStorage.getItem("adminToken");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      await axios.delete(`${API}/testimonials/${id}`, { headers });
      toast.success("Testimonial deleted successfully!");
      fetchTestimonials();
    } catch (error) {
      console.error("Error deleting testimonial:", error);
      toast.error("Failed to delete testimonial");
    }
  };

  return (
    <div data-testid="manage-testimonials">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Manage Testimonials</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-testimonial-button">
              <Plus size={20} className="mr-2" /> Add Testimonial
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="testimonial-dialog">
            <DialogHeader>
              <DialogTitle>Add Testimonial</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="client_name">Client Name</Label>
                <Input
                  id="client_name"
                  name="client_name"
                  data-testid="testimonial-name-input"
                  value={formData.client_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="content">Testimonial</Label>
                <Textarea
                  id="content"
                  name="content"
                  rows={4}
                  data-testid="testimonial-content-input"
                  value={formData.content}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="rating">Rating</Label>
                <Select
                  name="rating"
                  onValueChange={(value) => setFormData({ ...formData, rating: value })}
                  value={formData.rating}
                >
                  <SelectTrigger data-testid="testimonial-rating-select">
                    <SelectValue placeholder="Select rating" />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 4, 3, 2, 1].map((num) => (
                      <SelectItem key={num} value={num.toString()}>
                        {num} Stars
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="event_type">Event Type (Optional)</Label>
                <Input
                  id="event_type"
                  name="event_type"
                  data-testid="testimonial-event-input"
                  value={formData.event_type}
                  onChange={handleChange}
                  placeholder="e.g., Wedding, Corporate Event"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="testimonial-submit-button">
                Add Testimonial
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {testimonials.map((testimonial, index) => (
          <Card key={testimonial.id} data-testid={`testimonial-card-${index}`}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{testimonial.client_name}</CardTitle>
                  {testimonial.event_type && (
                    <p className="text-sm text-gray-600">{testimonial.event_type}</p>
                  )}
                </div>
                <div className="flex">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} fill="#d97706" className="text-[#8B7355]" />
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 text-sm mb-4 italic">"{testimonial.content}"</p>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleDelete(testimonial.id)}
                data-testid={`delete-testimonial-${index}`}
              >
                <Trash2 size={16} className="mr-2" /> Delete
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {testimonials.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600">No testimonials yet. Click "Add Testimonial" to create one.</p>
        </div>
      )}
    </div>
  );
};

export default ManageTestimonials;
